import React from 'react';

import { IconButton, TableCell, TableRow, Typography, useTheme } from '@mui/material';
import clsx from 'clsx';
import { AccountAvatar, PermissionRoleMenu } from 'src/components';
import { TrashcanIcon } from 'src/components/icons/TrashcanIcon';
import { Apollo } from 'src/gql/generated';
import { getDocumentPermissionRoles, getEntityDisplayDetails } from 'src/helpers';
import { useDeleteStorablePermission, useUpsertStorablePermissions } from 'src/hooks';
import type { AccountSubset } from 'src/types';
import { makeStyles } from 'tss-react/mui';

export const permissionListCellHeight = 48;

export const usePermissionRowStyles = makeStyles()(theme => ({
  authorCellWrapper: {
    alignItems: 'baseline',
    display: 'flex',
  },
  divider: {
    height: theme.spacing(1),
    margin: theme.spacing(0, 1),
  },
  nameCell: {
    flexGrow: 1,
  },
  optionSubtext: {
    color: theme.appColors.secondaryType,
    fontFamily: theme.typography.fontFamily,
    fontSize: 14,
  },
  permissionRoleMenu: {
    height: 24,
  },
  roleCell: {
    alignItems: 'center',
    borderBottom: 'unset',
    boxSizing: 'border-box',
    display: 'flex',
    height: permissionListCellHeight,
    justifyContent: 'flex-end',
    minWidth: 140,
  },
  roleText: {
    paddingRight: theme.spacing(1),
  },
  tableCell: {
    alignItems: 'center',
    borderBottom: 'unset',
    boxSizing: 'border-box',
    display: 'flex',
    height: permissionListCellHeight,
  },
  tableRow: {
    '&:hover': {
      backgroundColor: theme.appColors.hoverBackground,
    },
    cursor: 'default !important',
    display: 'flex',
    width: '100%',
  },
}));

interface StorablePermissionRowType extends Pick<
  Apollo.StorablePermission,
  'id' | 'role' | '__typename'
> {
  account: AccountSubset & { accountType: Apollo.Account['accountType']; id: UUID };
  storable: Pick<Apollo.Storable, 'id' | '__typename'>;
}

interface PermissionListRowProps {
  permission: StorablePermissionRowType;
  disabled?: boolean;
}

export function StorablePermissionRow({
  disabled,
  permission,
}: PermissionListRowProps): JSX.Element {
  const theme = useTheme();
  const { classes } = usePermissionRowStyles();
  const { account, id, role, storable } = permission;
  const { deleteStorablePermission } = useDeleteStorablePermission();
  const { upsertStorablePermissions } = useUpsertStorablePermissions();
  const permissionRoles = getDocumentPermissionRoles();
  const { primary: accountLabel, secondary: accountSubLabel } = getEntityDisplayDetails(account);

  async function handleDelete(): Promise<void> {
    await deleteStorablePermission({
      variables: { permissionId: id },
    });
  }

  async function handlePermissionChange(newRole: Apollo.PermissionRole): Promise<void> {
    await upsertStorablePermissions({
      variables: {
        input: {
          notifyRecipients: false,
          permissions: [
            {
              accountId: account.id,
              accountType: account.accountType,
              origin: Apollo.PermissionOrigin.User,
              role: newRole,
            },
          ],
          storableId: storable.id,
        },
      },
    });
  }

  return (
    <TableRow className={classes.tableRow}>
      <TableCell className={classes.tableCell} style={{ paddingRight: theme.spacing(1) }}>
        <AccountAvatar account={account} size="sm" />
      </TableCell>
      <TableCell className={clsx(classes.tableCell, classes.nameCell)}>
        <div className={classes.authorCellWrapper} style={{ flexDirection: 'column' }}>
          <Typography variant="body1">{accountLabel}</Typography>
          {accountSubLabel && accountSubLabel !== accountLabel && (
            <Typography variant="caption">{accountSubLabel}</Typography>
          )}
        </div>
      </TableCell>
      <TableCell className={classes.roleCell} style={{ paddingRight: theme.spacing(1) }}>
        <PermissionRoleMenu
          MenuProps={{
            anchorOrigin: { horizontal: 'right', vertical: 'top' },
            transformOrigin: { horizontal: 'right', vertical: 'top' },
            variant: 'menu',
          }}
          border={false}
          className={classes.permissionRoleMenu}
          disabled={disabled}
          enabledRoles={permissionRoles}
          handleSelect={handlePermissionChange}
          value={role}
        />
      </TableCell>
      <TableCell className={classes.tableCell}>
        <IconButton disabled={disabled} size="small" onClick={handleDelete}>
          <TrashcanIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}
